import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ConsumedVoucherDetails,
  GetVouchersRequest,
  Voucher,
  VoucherData,
  VoucherProduct
} from './types';
import * as thunks from './thunks';
import { PaginationMeta, PaginationV2 } from 'src/types';

type State = {
  voucherDetails: VoucherData;
  products: VoucherProduct[];
  productsMeta: PaginationV2;
  vouchersList: Voucher[];
  isListLoading: boolean;
  isLoading: boolean;
  isProductsLoading: boolean;
  filter: GetVouchersRequest;
  meta: PaginationMeta;
  consumedVoucherDetails: ConsumedVoucherDetails[];
  isConsumedListLoading: boolean;
};

const initialState: State = {
  vouchersList: [],
  products: [],
  productsMeta: {},
  isProductsLoading: false,
  isListLoading: false,
  isLoading: false,
  filter: {},
  voucherDetails: {},
  meta: {},
  consumedVoucherDetails: [],
  isConsumedListLoading: false,
};

const slice = createSlice({
  name: 'voucher',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<GetVouchersRequest>) => {
      state.filter = { ...state.filter, ...action.payload };
    },

    setIsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(thunks.updateVoucherThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(thunks.updateVoucherThunk.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(thunks.updateVoucherThunk.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(thunks.getVoucherDataThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(thunks.getVoucherDataThunk.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(
      thunks.getVoucherDataThunk.fulfilled,
      (state, { payload }) => {
        const { data } = payload?.originalData ?? {};
        state.voucherDetails = {
          ...data,
          // Remove domain for icon url
          voucher_icon_url: data?.voucher_icon_url?.replace(
            /^https?:\/\/[^/]+\/+/,
            ''
          ),

          // fall back for number values, intended to be in string format
          amount: data?.amount ?? '0',
          min_spend: data?.minimum_spend ?? '0',
          cap_amount: data?.cap_amount ?? '0'
        };
        state.isLoading = false;
      }
    );
    builder.addCase(thunks.getVouchersThunk.pending, (state) => {
      state.isListLoading = true;
    });
    builder.addCase(thunks.getVouchersThunk.fulfilled, (state, { payload }) => {
      const { data, meta } = payload.originalData;
      state.vouchersList = data || [];
      state.meta = meta || {};
      state.isListLoading = false;
    });
    builder.addCase(thunks.getVouchersThunk.rejected, (state) => {
      state.isListLoading = false;
    });

    builder.addCase(thunks.getVoucherProductsThunk.pending, (state) => {
      state.isProductsLoading = true;
    });
    builder.addCase(
      thunks.getVoucherProductsThunk.fulfilled,
      (state, { payload }) => {
        const { data, meta } = payload.originalData;
        state.products = data || [];
        state.productsMeta = meta || {};
        state.isProductsLoading = false;
      }
    );
    builder.addCase(thunks.getVoucherProductsThunk.rejected, (state) => {
      state.isProductsLoading = false;
    });
    builder.addCase(thunks.getConsumedVoucherTransactions.pending, (state) => {
      state.isConsumedListLoading = true;
    });
    builder.addCase(thunks.getConsumedVoucherTransactions.fulfilled, (state, { payload }) => {
      const { data } = payload.originalData;
      state.consumedVoucherDetails= data || [];
      state.isConsumedListLoading = false;
    });
    builder.addCase(thunks.getConsumedVoucherTransactions.rejected, (state) => {
      state.isConsumedListLoading = false;
    });
  }
});

export const reducer = slice.reducer;

export const actions = {
  ...slice.actions,
  ...thunks
};
